// =====================================================================
//  CAPA DE DATOS — un hook por vista o tabla
//
//  Todas las lecturas van contra las vistas del esquema, que llevan
//  security_invoker y por lo tanto respetan RLS. La consecuencia práctica
//  es que no hay que filtrar por permisos acá: lo que el rol no puede ver,
//  no llega.
//
//  Las escrituras pasan por mutar(), que trata «cero filas afectadas»
//  como falta de permiso (ver src/lib/supabase.ts).
// =====================================================================

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { supabase, mutar } from '../lib/supabase'
import type {
  Alerta,
  Articulo,
  Bitacora,
  CambioSeguimiento,
  Causa,
  ChecklistEstado,
  ChecklistItem,
  Comuna,
  Concesion,
  Criterio,
  DueDiligence,
  EstadoCausa,
  Hito,
  Lote,
  LoteDefinido,
  Presupuesto,
  Puntaje,
  PuntajeCriterio,
  RolDescripcion,
  RolUsuario,
  TipoRol,
  Usuario,
} from '../tipos/dominio'

// ---------------------------------------------------------------------
//  Claves de caché
// ---------------------------------------------------------------------
export const CLAVES = {
  perfil: ['perfil'] as const,
  causa: ['causa'] as const,
  concesiones: ['concesiones'] as const,
  puntajes: ['puntajes'] as const,
  criterios: ['criterios'] as const,
  presupuesto: ['presupuesto'] as const,
  lotes: ['lotes'] as const,
  lotesDefinidos: ['lotesDefinidos'] as const,
  hitos: ['hitos'] as const,
  comunas: ['comunas'] as const,
  usuarios: ['usuarios'] as const,
  articulos: ['articulos'] as const,
  roles: ['roles'] as const,
  alertas: ['alertas'] as const,
  dueDiligence: ['dueDiligence'] as const,
  checklistItems: ['checklistItems'] as const,
  detalle: (id: number) => ['detallePuntaje', id] as const,
  checklist: (id: number) => ['checklist', id] as const,
  historial: (id: number) => ['historial', id] as const,
  bitacora: ['bitacora'] as const,
}

/**
 * Lo que hay que refrescar después de tocar el seguimiento de una
 * concesión. El puntaje, el presupuesto, los lotes y las alertas se
 * derivan del estado y del interés, así que cambian con él.
 */
function invalidarDerivados(qc: QueryClient, concesionId?: number) {
  qc.invalidateQueries({ queryKey: CLAVES.concesiones })
  qc.invalidateQueries({ queryKey: CLAVES.puntajes })
  qc.invalidateQueries({ queryKey: CLAVES.presupuesto })
  qc.invalidateQueries({ queryKey: CLAVES.lotes })
  qc.invalidateQueries({ queryKey: CLAVES.lotesDefinidos })
  qc.invalidateQueries({ queryKey: CLAVES.alertas })
  qc.invalidateQueries({ queryKey: CLAVES.bitacora })
  if (concesionId !== undefined) {
    qc.invalidateQueries({ queryKey: CLAVES.detalle(concesionId) })
    qc.invalidateQueries({ queryKey: CLAVES.historial(concesionId) })
    qc.invalidateQueries({ queryKey: CLAVES.dueDiligence })
  }
}

/**
 * Ejecuta una lectura y devuelve las filas con el tipo del dominio.
 *
 * Sobre el cast: el cliente va tipado con el esquema real, así que los
 * nombres de tabla, vista y columna se validan al compilar — eso es lo
 * que se quiere de los tipos generados. Pero Postgres NO puede probar la
 * no-nulidad a través de una vista, así que el generador marca todas sus
 * columnas como nullable, incluida `v_concesion.id`. Consumir eso
 * obligaría a un `?? 0` en cada campo de cada vista, escondiendo los
 * nulos que sí importan entre decenas que nunca ocurren.
 *
 * Los tipos de src/tipos/dominio.ts son la versión estrecha y verdadera:
 * dicen qué campos pueden faltar de verdad. La conversión ocurre acá, en
 * un solo lugar, y las pruebas de paridad verifican que las formas
 * coincidan con lo que las vistas devuelven.
 */
const pedir = async <T>(consulta: PromiseLike<{ data: unknown; error: unknown }>): Promise<T> => {
  const { data, error } = await consulta
  if (error) throw error
  return (data ?? []) as T
}

/** La misma conversión para las consultas de una sola fila. */
const pedirUna = async <T>(
  consulta: PromiseLike<{ data: unknown; error: unknown }>,
): Promise<T | null> => {
  const { data, error } = await consulta
  if (error) throw error
  return (data ?? null) as T | null
}

// ---------------------------------------------------------------------
//  Identidad
// ---------------------------------------------------------------------

/**
 * Perfil del usuario en sesión.
 *
 * Devuelve null si el correo de Google no está en la tabla `usuario`: RLS
 * no le deja ver ninguna fila. Es la señal de «no autorizado».
 *
 * El filtro por `auth_id` NO es redundante con RLS. Un LECTOR o un
 * ANALISTA solo ve su propia fila, así que sin filtro igual funcionaría;
 * pero un MÁSTER ve a TODOS los usuarios, y entonces maybeSingle()
 * recibiría varias filas y PostgREST devolvería PGRST116. Es decir: sin
 * este filtro, el máster —y solo el máster— no podría entrar.
 */
export function usePerfil(authId: string | undefined) {
  return useQuery({
    queryKey: [...CLAVES.perfil, authId],
    enabled: !!authId,
    queryFn: () =>
      pedirUna<Usuario>(supabase.from('usuario').select('*').eq('auth_id', authId!).maybeSingle()),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

// ---------------------------------------------------------------------
//  Lecturas
// ---------------------------------------------------------------------

export function useCausa() {
  return useQuery({
    queryKey: CLAVES.causa,
    queryFn: async (): Promise<Causa | null> => {
      const { data, error } = await supabase.from('causa').select('*').limit(1).maybeSingle()
      if (error) throw error
      return data
    },
    staleTime: 5 * 60_000,
  })
}

export function useConcesiones() {
  return useQuery({
    queryKey: CLAVES.concesiones,
    queryFn: () =>
      pedir<Concesion[]>(supabase.from('v_concesion').select('*').order('id')),
  })
}

export function usePuntajes() {
  return useQuery({
    queryKey: CLAVES.puntajes,
    queryFn: () => pedir<Puntaje[]>(supabase.from('v_puntaje').select('*')),
  })
}

export function useCriterios() {
  return useQuery({
    queryKey: CLAVES.criterios,
    queryFn: () =>
      pedir<Criterio[]>(supabase.from('criterio').select('*').eq('activo', true).order('orden')),
    staleTime: 60_000,
  })
}

export function usePresupuesto() {
  return useQuery({
    queryKey: CLAVES.presupuesto,
    queryFn: () =>
      pedirUna<Presupuesto>(supabase.from('v_presupuesto').select('*').limit(1).maybeSingle()),
  })
}

/** Agrupación calculada: un lote por grupo, con sus totales. */
export function useLotes() {
  return useQuery({
    queryKey: CLAVES.lotes,
    queryFn: () =>
      pedir<Lote[]>(supabase.from('v_lote').select('*').order('concesiones', { ascending: false })),
  })
}

/**
 * El catálogo de lotes definidos, desde la tabla.
 *
 * NO se puede usar v_lote para esto: esa vista se construye desde
 * `concesion`, así que un lote recién creado —todavía sin concesiones
 * asignadas— no aparece. Si el desplegable de asignación se alimentara de
 * ahí, nunca se le podría asignar la primera concesión a un lote nuevo.
 */
export function useLotesDefinidos() {
  return useQuery({
    queryKey: CLAVES.lotesDefinidos,
    queryFn: () => pedir<LoteDefinido[]>(supabase.from('lote').select('*').order('codigo')),
  })
}

export function useHitos() {
  return useQuery({
    queryKey: CLAVES.hitos,
    queryFn: () =>
      pedir<Hito[]>(supabase.from('hito').select('*').order('fecha').order('orden')),
  })
}

export function useComunas() {
  return useQuery({
    queryKey: CLAVES.comunas,
    queryFn: () => pedir<Comuna[]>(supabase.from('comuna').select('*').order('nombre')),
  })
}

/**
 * Los usuarios registrados.
 *
 * Un LECTOR o un ANALISTA solo ve su propia fila; el MÁSTER ve todas. No
 * hace falta filtrar acá: RLS ya lo hace, y la vista de administración es
 * de todos modos solo para el máster.
 */
export function useUsuarios() {
  return useQuery({
    queryKey: CLAVES.usuarios,
    queryFn: () => pedir<Usuario[]>(supabase.from('usuario').select('*').order('email')),
  })
}

/** El resumen de los artículos 147 a 152. Editable por el máster. */
export function useArticulos() {
  return useQuery({
    queryKey: CLAVES.articulos,
    queryFn: () =>
      pedir<Articulo[]>(
        supabase.from('articulo').select('*').eq('activo', true).order('orden'),
      ),
    staleTime: 5 * 60_000,
  })
}

/** Qué puede hacer cada rol, para no repetirlo en la interfaz. */
export function useRoles() {
  return useQuery({
    queryKey: CLAVES.roles,
    queryFn: () =>
      pedir<RolDescripcion[]>(supabase.from('rol_descripcion').select('*').order('orden')),
    staleTime: 5 * 60_000,
  })
}

export function useAlertas() {
  return useQuery({
    queryKey: CLAVES.alertas,
    queryFn: () => pedir<Alerta[]>(supabase.from('v_alertas').select('*')),
  })
}

export function useDueDiligence() {
  return useQuery({
    queryKey: CLAVES.dueDiligence,
    queryFn: () => pedir<DueDiligence[]>(supabase.from('v_due_diligence').select('*')),
  })
}

export function useDetallePuntaje(concesionId: number | null) {
  return useQuery({
    queryKey: CLAVES.detalle(concesionId ?? 0),
    enabled: concesionId !== null,
    queryFn: () =>
      pedir<PuntajeCriterio[]>(
        supabase
          .from('v_puntaje_detalle')
          .select('*')
          .eq('concesion_id', concesionId!)
          .order('orden'),
      ),
  })
}

export function useChecklistItems() {
  return useQuery({
    queryKey: CLAVES.checklistItems,
    queryFn: () =>
      pedir<ChecklistItem[]>(
        supabase.from('checklist_item').select('*').eq('activo', true).order('orden'),
      ),
    staleTime: 5 * 60_000,
  })
}

export function useChecklist(concesionId: number | null) {
  return useQuery({
    queryKey: CLAVES.checklist(concesionId ?? 0),
    enabled: concesionId !== null,
    queryFn: () =>
      pedir<ChecklistEstado[]>(
        supabase.from('checklist_estado').select('*').eq('concesion_id', concesionId!),
      ),
  })
}

export function useHistorial(concesionId: number | null) {
  return useQuery({
    queryKey: CLAVES.historial(concesionId ?? 0),
    enabled: concesionId !== null,
    queryFn: () =>
      pedir<Bitacora[]>(
        supabase
          .from('bitacora')
          .select('*')
          .eq('concesion_id', concesionId!)
          .order('id', { ascending: false }),
      ),
  })
}

export function useBitacora(limite = 300) {
  return useQuery({
    queryKey: CLAVES.bitacora,
    queryFn: () =>
      pedir<Bitacora[]>(
        supabase.from('bitacora').select('*').order('id', { ascending: false }).limit(limite),
      ),
  })
}

// ---------------------------------------------------------------------
//  Escrituras
// ---------------------------------------------------------------------

/**
 * Actualiza el seguimiento de una concesión.
 *
 * Los errores de los triggers (postura bajo el mínimo, RETIRADA sin
 * acreditar el doble) suben tal cual para que la vista los traduzca con
 * leerError(). No se intenta validar acá lo que el motor ya valida: si la
 * regla vive en un solo lugar, no puede quedar desincronizada.
 */
export function useGuardarSeguimiento() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, cambio }: { id: number; cambio: CambioSeguimiento }) =>
      mutar(
        supabase.from('seguimiento').update(cambio).eq('concesion_id', id).select('concesion_id'),
      ),
    onSuccess: (_d, { id }) => invalidarDerivados(qc, id),
  })
}

export function useGuardarEvaluacion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      criterio,
      valor,
    }: {
      id: number
      criterio: string
      valor: number
    }) =>
      mutar(
        supabase
          .from('evaluacion')
          .upsert(
            { concesion_id: id, criterio, valor },
            { onConflict: 'concesion_id,criterio' },
          )
          .select('criterio'),
      ),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: CLAVES.puntajes })
      qc.invalidateQueries({ queryKey: CLAVES.detalle(id) })
    },
  })
}

export function useGuardarChecklist() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      itemId,
      cumplido,
    }: {
      id: number
      itemId: number
      cumplido: boolean
    }) =>
      mutar(
        supabase
          .from('checklist_estado')
          .upsert(
            { concesion_id: id, item_id: itemId, cumplido },
            { onConflict: 'concesion_id,item_id' },
          )
          .select('item_id'),
      ),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: CLAVES.checklist(id) })
      qc.invalidateQueries({ queryKey: CLAVES.dueDiligence })
    },
  })
}

/**
 * Marca la vigencia verificada — el ítem 10 del due diligence.
 *
 * No es una formalidad: el titular puede retirar la concesión pagando el
 * doble hasta el momento mismo del remate, así que hay que confirmar que
 * sigue en el listado antes de ir.
 */
export function useVerificarVigencia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) =>
      mutar(
        supabase
          .from('seguimiento')
          .update({ verificado_en: new Date().toISOString() })
          .eq('concesion_id', id)
          .select('concesion_id'),
      ),
    onSuccess: (_d, id) => invalidarDerivados(qc, id),
  })
}

// --- Lotes (art. 148) — solo máster ---------------------------------

export function useCrearLote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      causaId,
      codigo,
      descripcion,
    }: {
      causaId: number
      codigo: string
      descripcion?: string
    }) =>
      mutar(
        supabase
          .from('lote')
          .insert({ causa_id: causaId, codigo, descripcion: descripcion ?? null })
          .select('id, codigo'),
      ),
    onSuccess: () => invalidarDerivados(qc),
  })
}

export function useAsignarLote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, loteId }: { id: number; loteId: number | null }) =>
      mutar(supabase.from('concesion').update({ lote_id: loteId }).eq('id', id).select('id')),
    onSuccess: () => invalidarDerivados(qc),
  })
}

export function useConfirmarLote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ loteId, confirmado }: { loteId: number; confirmado: boolean }) =>
      mutar(supabase.from('lote').update({ confirmado }).eq('id', loteId).select('id')),
    onSuccess: () => invalidarDerivados(qc),
  })
}

// --- Usuarios — solo máster ------------------------------------------

/**
 * Registra un correo para que pueda entrar.
 *
 * No hace falta que la persona haya entrado nunca, ni que entre después:
 * los triggers del esquema enlazan la cuenta de Google en cualquiera de
 * los dos órdenes. Alcanza con el correo.
 */
export function useCrearUsuario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      email,
      nombre,
      rol,
    }: {
      email: string
      nombre?: string
      rol: RolUsuario
    }) =>
      mutar(
        supabase
          .from('usuario')
          .insert({ email: email.trim().toLowerCase(), nombre: nombre?.trim() || null, rol })
          .select('id, email, rol, auth_id'),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.usuarios }),
  })
}

/**
 * Cambia rol, nombre o acceso.
 *
 * `activo: false` es la forma correcta de quitar acceso. Borrar la fila
 * deja en `null` el «quién tocó esto» de cada ficha que esa persona haya
 * editado; desactivar no pierde nada de la atribución.
 */
export function useActualizarUsuario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      cambio,
    }: {
      id: number
      cambio: { rol?: RolUsuario; activo?: boolean; nombre?: string | null }
    }) => mutar(supabase.from('usuario').update(cambio).eq('id', id).select('id, email, rol, activo')),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.usuarios }),
  })
}

/**
 * Elimina a un usuario definitivamente.
 *
 * Distinto de desactivar. Desactivar conserva la atribución: las fichas que
 * esa persona editó siguen mostrando quién las tocó. Eliminar pone en
 * `null` ese dato en cada una, porque la clave ajena es `on delete set
 * null`.
 *
 * Lo que NO se pierde es la bitácora: no tiene claves ajenas, así que el
 * correo y el registro histórico quedan intactos aunque la fila desaparezca.
 *
 * El correo maestro no se puede eliminar — lo impide un trigger, que
 * responde MN700 incluso desde el editor SQL.
 */
export function useBorrarUsuario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) =>
      mutar(supabase.from('usuario').delete().eq('id', id).select('id, email')),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.usuarios }),
  })
}

// --- Causa, cronograma y marco legal — solo máster -------------------

/**
 * Actualiza los datos del expediente.
 *
 * El campo que de verdad importa es `fecha_publicacion`: hoy está vacío y
 * cargarlo es la acción más importante de septiembre, porque la
 * publicación en el Diario Oficial fija los lotes definitivos.
 *
 * El motor la valida: si deja menos de 30 días corridos antes del remate,
 * la rechaza con MN147 (art. 147).
 */
export function useGuardarCausa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      cambio,
    }: {
      id: number
      cambio: Partial<{
        fecha_publicacion: string | null
        fecha_remate: string
        estado: EstadoCausa
        tribunal: string
        caratulado: string
        demandante: string | null
        abogado_patrocinante: string | null
        enlace_remate: string | null
      }>
    }) => mutar(supabase.from('causa').update(cambio).eq('id', id).select('id, rol')),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CLAVES.causa })
      // La fecha de remate y la de publicación cambian los plazos que
      // muestra el panel y el cronograma.
      qc.invalidateQueries({ queryKey: CLAVES.concesiones })
      qc.invalidateQueries({ queryKey: CLAVES.hitos })
    },
  })
}

export function useCrearHito() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (hito: {
      causa_id: number
      fecha: string
      titulo: string
      detalle?: string | null
      articulo?: string | null
      orden?: number
    }) => mutar(supabase.from('hito').insert(hito).select('id, titulo')),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.hitos }),
  })
}

export function useGuardarHito() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      cambio,
    }: {
      id: number
      cambio: Partial<{ fecha: string; titulo: string; detalle: string | null; articulo: string | null }>
    }) => mutar(supabase.from('hito').update(cambio).eq('id', id).select('id')),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.hitos }),
  })
}

export function useBorrarHito() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) =>
      mutar(supabase.from('hito').delete().eq('id', id).select('id')),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.hitos }),
  })
}

export function useGuardarArticulo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, texto }: { id: number; texto: string }) =>
      mutar(supabase.from('articulo').update({ texto }).eq('id', id).select('id, codigo')),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.articulos }),
  })
}

// --- Territorio — solo máster ----------------------------------------

export function useCrearComuna() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ nombre, provincia }: { nombre: string; provincia: string }) =>
      mutar(
        supabase
          .from('comuna')
          .insert({
            nombre: nombre.trim().toUpperCase(),
            provincia: provincia.trim(),
            activa: false,
          })
          .select('id, nombre'),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.comunas }),
  })
}

export function useActualizarComuna() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, activa }: { id: number; activa: boolean }) =>
      mutar(supabase.from('comuna').update({ activa }).eq('id', id).select('id')),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.comunas }),
  })
}

// --- Pesos del puntaje — solo máster ---------------------------------

export function useGuardarPeso() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ codigo, peso }: { codigo: string; peso: number }) =>
      mutar(supabase.from('criterio').update({ peso }).eq('codigo', codigo).select('codigo, peso')),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CLAVES.criterios })
      qc.invalidateQueries({ queryKey: CLAVES.puntajes })
      qc.invalidateQueries({ queryKey: ['detallePuntaje'] })
    },
  })
}

// --- Corrección de los datos de la nómina — solo máster --------------

/**
 * Corrige los datos que vinieron de la nómina TGR.
 *
 * `concesion` es, en principio, un calco de lo que remitió la Tesorería y
 * no se toca. Pero hay casos en que hay que corregirlo, y el más concreto
 * está en el listado: GEMINIS 1/59 figura con $1.146.180 girados y $0
 * adeudados, lo que no tiene sentido. Cuando llegue el certificado de
 * deuda de la TGR habrá que cargar el monto real, y ahí el mínimo de
 * subasta y todo el puntaje se recalculan solos.
 *
 * Cada cambio acá mueve la aritmética del remate, así que queda
 * restringido al máster por RLS.
 */
export function useCorregirConcesion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      cambio,
    }: {
      id: number
      cambio: Partial<{
        nombre: string
        ubicacion: string | null
        titular: string
        monto_girado: number
        monto_adeudado: number
        utm_adeudadas: number
        rol_tipo: TipoRol
        rol_numero: string | null
        rol_anio: number | null
      }>
    }) => mutar(supabase.from('concesion').update(cambio).eq('id', id).select('id, nombre')),
    onSuccess: () => invalidarDerivados(qc),
  })
}

/**
 * Fija el presupuesto.
 *
 * Inserta una fila nueva en vez de actualizar la anterior: `presupuesto`
 * es un historial, y el vigente es el de `vigente_desde` más reciente.
 * Así queda registro de con qué cifra se estaba trabajando en cada
 * momento, y bajar el presupuesto toma efecto de inmediato (PLAN §5.3).
 */
export function useFijarPresupuesto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      causaId,
      monto,
      glosa,
    }: {
      causaId: number
      monto: number
      glosa?: string
    }) =>
      mutar(
        supabase
          .from('presupuesto')
          .insert({
            causa_id: causaId,
            monto,
            vigente_desde: new Date().toISOString().slice(0, 10),
            glosa: glosa ?? null,
          })
          .select('id, monto'),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CLAVES.presupuesto })
      qc.invalidateQueries({ queryKey: CLAVES.puntajes })
      // El criterio «calce» depende del presupuesto vigente.
      qc.invalidateQueries({ queryKey: ['detallePuntaje'] })
    },
  })
}
